/**
 * ImportarLegacyBeemModal — importador dedicado para el catálogo heredado Beem.
 *
 * Formato esperado del Excel: 1 hoja con columnas
 *   REFERENCIA | NOMBRE | DESCRIPCION | CANTIDAD INICIAL | VALOR COMPRA | DETAL | MAYORISTA
 *
 * Flujo:
 *   1. Usuario elige archivo .xlsx
 *   2. Backend hace /preview → mostramos resumen (nuevos, actualizar, categorías, patrón A/B)
 *   3. Usuario ve tabla scrolleable con las primeras 200 filas y confirma
 *   4. Backend hace /confirmar → toast con resultado
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Package, X } from "lucide-react";
import { toast } from "sonner";

export default function ImportarLegacyBeemModal({ open, onOpenChange, onImported }) {
  const [file, setFile] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setPreviewing(false);
    setConfirming(false);
  };

  const close = () => {
    reset();
    onOpenChange?.(false);
  };

  const analizar = async () => {
    if (!file) {
      toast.error("Selecciona el archivo primero");
      return;
    }
    setPreviewing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/productos/importar-legacy-beem/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Detecté ${data.resumen.total} productos (${data.resumen.nuevos} nuevos)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo analizar el archivo");
    } finally {
      setPreviewing(false);
    }
  };

  const confirmar = async () => {
    if (!preview) return;
    setConfirming(true);
    try {
      const filas = preview.filas.map((f) => ({
        sku_generado: f.sku_generado,
        nombre_final: f.nombre_final,
        categoria: f.categoria,
        familia_key: f.familia_key || null,
        color_detectado: f.color_detectado || null,
        cantidad: f.cantidad,
        costo: f.costo,
        precio_sugerido: f.precio_sugerido,
        precio_minimo: f.precio_minimo,
        existe: f.existe,
      }));
      const { data } = await api.post("/productos/importar-legacy-beem/confirmar", { filas });
      const msg = `${data.creados} creados · ${data.actualizados} actualizados${
        data.errores?.length ? ` · ${data.errores.length} con error` : ""
      }`;
      toast.success(msg);
      onImported?.();
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo importar");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="import-legacy-beem-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase text-emerald-700 tracking-wider">Importador Beem</div>
              <DialogTitle className="font-display text-2xl mt-1">Importar inventario</DialogTitle>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Sube el Excel con las 7 columnas: <b>REFERENCIA · NOMBRE · DESCRIPCION · CANTIDAD INICIAL · VALOR COMPRA · DETAL · MAYORISTA</b>.
            Los precios ya vienen listos, no se aplica ninguna estrategia de costo. El sistema detecta automáticamente
            productos con SKU único y variantes por color, y respeta las imágenes de productos que ya existen en tu catálogo.
          </p>
        </DialogHeader>

        {/* Paso 1 — archivo */}
        {!preview && (
          <div className="mt-4 space-y-4">
            <label className="block p-6 border-2 border-dashed border-border rounded-xl bg-muted/20 hover:bg-muted/40 cursor-pointer transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="legacy-beem-file-input"
              />
              <div className="flex items-center gap-3">
                <Upload className="w-6 h-6 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-foreground">
                    {file ? file.name : "Elegir archivo Excel"}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {file ? `${(file.size / 1024).toFixed(0)} KB · Click para cambiar` : "Formato .xlsx · Máx 20MB"}
                  </div>
                </div>
              </div>
            </label>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={close}>Cancelar</Button>
              <Button
                type="button"
                onClick={analizar}
                disabled={!file || previewing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="legacy-beem-analizar-btn"
              >
                {previewing ? "Analizando..." : "Analizar archivo"}
              </Button>
            </div>
          </div>
        )}

        {/* Paso 2 — preview */}
        {preview && (
          <div className="mt-4 space-y-4">
            {/* Resumen */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <ResumenCard label="Total filas" value={preview.resumen.total} icon={Package} tone="slate" />
              <ResumenCard label="Nuevos" value={preview.resumen.nuevos} icon={CheckCircle2} tone="emerald" />
              <ResumenCard label="Actualizar" value={preview.resumen.actualizar} icon={AlertTriangle} tone="amber" />
              <ResumenCard label="Categorías nuevas" value={preview.resumen.categorias_a_crear.length} icon={Package} tone="blue" />
            </div>

            {/* Distribución patrón + agrupamiento por familia/color */}
            <div className="p-3 rounded-lg bg-muted/40 border border-border text-xs text-muted-foreground space-y-1">
              <div>
                <b>Patrón A</b> (SKU real, 1 producto por fila): <b>{preview.resumen.patron_a}</b> ·
                &nbsp;<b>Patrón B</b> (variantes por color con SKU autogenerado): <b>{preview.resumen.patron_b}</b>
              </div>
              {preview.resumen.familias_detectadas !== undefined && (
                <div className="text-emerald-800">
                  🎨 <b>{preview.resumen.familias_detectadas}</b> familias detectadas ·
                  &nbsp;<b>{preview.resumen.familias_con_variantes}</b> con variantes de color ·
                  &nbsp;<b>{preview.resumen.filas_con_color_detectado}</b> productos con color reconocido
                </div>
              )}
            </div>

            {/* Preview de las primeras 200 filas */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="max-h-[50vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="legacy-beem-preview-table">
                  <thead className="bg-muted/60 sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold">Fila</th>
                      <th className="px-2 py-2 text-left font-semibold">Patr.</th>
                      <th className="px-2 py-2 text-left font-semibold">SKU</th>
                      <th className="px-2 py-2 text-left font-semibold">Nombre final</th>
                      <th className="px-2 py-2 text-left font-semibold">Categoría</th>
                      <th className="px-2 py-2 text-right font-semibold">Stock</th>
                      <th className="px-2 py-2 text-right font-semibold">Costo</th>
                      <th className="px-2 py-2 text-right font-semibold">Sugerido</th>
                      <th className="px-2 py-2 text-right font-semibold">Mínimo</th>
                      <th className="px-2 py-2 text-center font-semibold">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.filas.slice(0, 200).map((f) => (
                      <tr key={`${f.row_idx}-${f.sku_generado}`} className="border-t border-border/40 hover:bg-muted/30">
                        <td className="px-2 py-1.5 text-muted-foreground">{f.row_idx}</td>
                        <td className="px-2 py-1.5">
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                            f.patron === "A" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                          }`}>{f.patron}</span>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-[11px]">{f.sku_generado}</td>
                        <td className="px-2 py-1.5 max-w-[300px] truncate" title={f.nombre_final}>{f.nombre_final}</td>
                        <td className="px-2 py-1.5 text-muted-foreground">
                          {f.categoria || "-"}
                          {f.categoria_es_nueva && <span className="ml-1 text-[9px] text-emerald-700">•nueva</span>}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{f.cantidad.toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">${f.costo.toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">${f.precio_sugerido.toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">${f.precio_minimo.toLocaleString()}</td>
                        <td className="px-2 py-1.5 text-center">
                          {f.existe ? (
                            <span className="text-[10px] font-bold text-amber-700">ACTUALIZAR</span>
                          ) : (
                            <span className="text-[10px] font-bold text-emerald-700">NUEVO</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.filas.length > 200 && (
                <div className="px-3 py-2 bg-muted/40 text-[11px] text-muted-foreground text-center border-t border-border">
                  Mostrando 200 de {preview.filas.length} filas. Al confirmar se importan todas.
                </div>
              )}
            </div>

            {preview.resumen.categorias_a_crear.length > 0 && (
              <div className="p-3 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-900">
                <b>Se crearán {preview.resumen.categorias_a_crear.length} categorías nuevas:</b>{" "}
                {preview.resumen.categorias_a_crear.slice(0, 12).join(", ")}
                {preview.resumen.categorias_a_crear.length > 12 && "..."}
              </div>
            )}

            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
              <b>Regla de imágenes:</b> los productos que ya existan mantienen su foto actual. Los nuevos
              quedan sin foto — el operador la sube después desde <i>Productos</i>.
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setPreview(null)} data-testid="legacy-beem-volver-btn">
                <X className="w-4 h-4 mr-1" /> Volver
              </Button>
              <Button
                type="button"
                onClick={confirmar}
                disabled={confirming}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="legacy-beem-confirmar-btn"
              >
                {confirming ? "Importando..." : `Importar ${preview.resumen.total} productos`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResumenCard({ label, value, icon: Icon, tone }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
  };
  return (
    <div className={`p-3 rounded-lg ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4" />
        <div className="text-[10px] font-bold uppercase tracking-wider">{label}</div>
      </div>
      <div className="text-2xl font-display font-bold mt-1 tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}
